import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import trackProductEvent from '../../utils/trackProductEvent';
import imageActionNames from './imageActionNames';

// Should be fired when a visitor initially views an ungated PDP
// This will help us track
// # of visitors landing on our ungated PDPs through SEO or paid marketing
// SKUs with the highest click through rate
// # and % of visitors that land on an OOS SKU and product

const eventName = 'ungated.pdp';
const canonicalName = 'pdp';
const actionDetail = 'ungated_shop';

// eslint-disable-next-line import/prefer-default-export
export const trackPageView = ({
  selectedSkuId,
  externalProductId,
  recommendedCollections,
  isSkuOutOfStock,
  isSkuLimitedStock,
  allSkusOutOfStock,
  subSource,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const relatedItemSkuIds = recommendedCollections
    ?.find(collection => collection?.treatment === 'similar_items')
    ?.shopProducts?.nodes?.map(item => item.variantBySkuId?.sku);

  const params = new URLSearchParams(window.location.search);
  const actionType = 'view';
  const actionName = 'pdp_view';
  const directBuyContext = {
    shopping_context: null,
    outfit_id: null,
    related_item_sku_ids: relatedItemSkuIds || [],
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
    all_items_out_of_stock: allSkusOutOfStock,
    on_screen: {
      out_of_stock: isSkuOutOfStock,
      limited_stock: isSkuLimitedStock,
      hide_item: false,
    },
  };

  const botUserAgent = /bot|crawler|spider|robot|crawling/i.test(
    window.navigator.userAgent,
  );
  // metrics tags are pushed through to datadog
  // otherwise we only have access to 'actionName'
  const metricsTags = {
    client_business_line: clientBusinessLine,
    product_business_line: productBusinessLine,
    sku_out_of_stock: isSkuOutOfStock,
    all_items_out_of_stock: allSkusOutOfStock,
    utm_source: params.get('utm_source'),
    bot_user_agent: botUserAgent,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

export const trackInventoryAvailabilityEvent = ({
  collectionName,
  outfitId,
  selectedSkuId,
  source,
  sizeOptions,
  externalProductId,
  gated,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const allItemsOutOfStock = sizeOptions.every(
    sku => sku.availability === false,
  );
  const formattedSkus = sizeOptions.map(sku => ({
    out_of_stock: sku.availability === false,
    size: sku.label,
  }));

  const actionType = 'page_view';
  const actionName = 'oos_rate';
  const directBuyContext = {
    all_items_out_of_stock: allItemsOutOfStock,
    collection: collectionName,
    outfit_id: outfitId,
    source,
    sku_id: selectedSkuId,
    skus: formattedSkus,
    external_product_id: externalProductId,
  };

  trackProductEvent({
    eventName: 'pdp.all_sizes.view',
    canonicalName,
    actionType,
    actionName,
    actionDetail: gated ? undefined : 'ungated_shop',
    directBuyContext,
  });
};

// eslint-disable-next-line import/prefer-default-export
export const fireGtmPageViewEvent = state => {
  if (state.skipEventTracking) {
    return;
  }

  const eventAttributes = {
    pdp_type: state.gated ? 'gated' : 'ungated',
    sku_id: state.productSkuId,
    external_product_id: state.externalProductId,
    business_line: state.productBusinessLine,
    revenue: state.formattedPrice.replace('$', ''), // $ sign not needed
  };

  fireEvent({
    eventName: 'PDP_page_view',
    eventAttributes,
  });
};

// Below event is fired when a user clicks the Similar Styles jump link on the PDP

// eslint-disable-next-line import/prefer-default-export
export const trackSimilarItemsLinkClick = ({
  selectedSkuId,
  externalProductId,
  recommendedCollections,
  isSkuOutOfStock,
  isSkuLimitedStock,
  allSkusOutOfStock,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const relatedItemSkuIds = recommendedCollections
    ?.find(collection => collection?.treatment === 'similar_items')
    ?.shopProducts?.nodes?.map(item => item.variantBySkuId?.sku);

  const actionType = 'click';
  const actionName = 'link_to_carousel';
  const directBuyContext = {
    shopping_context: null,
    outfit_id: null,
    related_item_sku_ids: relatedItemSkuIds || [],
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: null,
    source: 'ungated_shop',
    all_items_out_of_stock: allSkusOutOfStock,
    on_screen: {
      out_of_stock: isSkuOutOfStock,
      limited_stock: isSkuLimitedStock,
      hide_item: false,
    },
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

/**
 *
 *
 * @param {*} state
 */
export const trackRelatedItemsViewed = ({
  selectedSkuId,
  relatedItems,
  subSource,
  externalProductId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'view';
  const actionName = 'carousel_view';
  const directBuyContext = {
    outfit_id: null,
    related_item_sku_ids: relatedItems.map(item => item?.variantBySkuId?.sku),
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

export const trackFirstRelatedItemsScroll = ({
  selectedSkuId,
  relatedItems,
  subSource,
  externalProductId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'scroll';
  const actionName = 'scroll_carousel';
  const directBuyContext = {
    outfit_id: null,
    related_item_sku_ids: relatedItems.map(item => item?.variantBySkuId?.sku),
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
    collection: null,
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

export const trackRelatedItemClick = ({
  relatedItems,
  clickedSku,
  subSource,
  externalProductId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'click';
  const actionName = 'carousel_click';
  const directBuyContext = {
    outfit_id: null,
    related_item_sku_ids: relatedItems.map(item => item?.variantBySkuId?.sku),
    sku_id: clickedSku,
    external_product_id: externalProductId || undefined,
    sub_source: subSource,
    source: 'ungated_shop',
    collection: null,
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

export const trackRelatedItemsEndCardViewed = ({
  selectedSkuId,
  relatedItems,
  subSource,
  externalProductId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'view';
  const actionName = 'carousel_end_card_view';
  const directBuyContext = {
    outfit_id: null,
    related_item_sku_ids: relatedItems.map(item => item?.variantBySkuId?.sku),
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

export const trackRelatedItemsEndCardClick = ({
  selectedSkuId,
  relatedItems,
  subSource,
  externalProductId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'click';
  const actionName = 'carousel_end_card_click';
  const directBuyContext = {
    outfit_id: null,
    related_item_sku_ids: relatedItems.map(item => item?.variantBySkuId?.sku),
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

export const trackRelatedItemsViewAllClick = ({
  selectedSkuId,
  relatedItems,
  subSource,
  externalProductId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'click';
  const actionName = 'carousel_view_all_click';
  const directBuyContext = {
    outfit_id: null,
    related_item_sku_ids: relatedItems.map(item => item?.variantBySkuId?.sku),
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

//  this event should fire when a customer sees a PDP with a mismatched business line
export const trackMismatchBizLine = ({
  selectedSkuId,
  externalProductId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }
  const actionType = 'view';
  // The action name is miss-leading because of a previous user experience for
  // mismatched user and pdp business lines
  const actionName = 'modal_view_business_line';
  const directBuyContext = {
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

//  this event should fire when a customer views a new focused image in the gallery
export const trackImageView = (
  { skuId, externalProductId, imageKey },
  skipEventTracking = false,
) => {
  if (skipEventTracking) {
    return;
  }
  // image.key is defined as `${image.category}-${i}` in ProductImageGallery
  // imageActionNames sorts action names by image.category
  const imageCategory = imageKey.split('-')[0].toLowerCase();
  const actionName = imageActionNames[imageCategory];

  if (actionName === undefined) {
    return; // don't track event if not able to grab a useful image action name
  }

  const actionType = 'view';
  const directBuyContext = {
    sku_id: skuId,
    external_product_id: externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
  });
};

/**
 *
 *
 * @param {*} state
 */
export const trackWaysToWearItCarouselViewed = ({
  selectedSkuId,
  waysToWearIt,
  subSource,
  externalProductId,
  pdpOutfitId,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'view';
  const actionName = 'ways_to_wear_it_view';
  const directBuyContext = {
    referring_outfit_id: pdpOutfitId,
    carousel_outfit_ids:
      waysToWearIt?.items?.nodes?.map(item => item.id) || null,
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
    collection: null,
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

/**
 *
 *
 * @param {*} state
 */
export const trackWaysToWearItOutfitViewed = ({
  selectedSkuId,
  waysToWearIt,
  externalProductId,
  subSource,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
  outfitId,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'view';
  const actionName = 'ways_to_wear_it_outfit_view';
  const directBuyContext = {
    outfit_id: outfitId,
    carousel_outfit_ids: waysToWearIt.items.nodes.map(item => item.id),
    sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
    collection: null,
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

/**
 *
 *
 * @param {*} state
 */
export const trackWaysToWearItCarouselAccordionChanged = ({
  selectedSkuId,
  externalProductId,
  waysToWearIt,
  subSource,
  expanded,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'click';
  const actionName = 'ways_to_wear_it_accordion_clicked';
  const directBuyContext = {
    carousel_outfit_ids: waysToWearIt.items.nodes.map(item => item.id),
    pdp_sku_id: selectedSkuId,
    external_product_id: externalProductId,
    sub_source: subSource,
    source: 'ungated_shop',
    collection: null,
    expanded,
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

/**
 *
 *
 * @param {*} state
 */
export const trackWaysToWearItOutfitItemClicked = ({
  selectedSkuId,
  clickedSkuId,
  externalProductId,
  outfitId,
  subSource,
  productBusinessLine,
  clientBusinessLine,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }

  const actionType = 'click';
  const actionName = 'ways_to_wear_it_outfit_item_clicked';
  const directBuyContext = {
    outfit_id: outfitId,
    sku_id: clickedSkuId,
    external_product_id: externalProductId,
    pdp_sku_id: selectedSkuId,
    sub_source: subSource,
    source: 'ungated_shop',
    collection: null,
  };

  const metricsTags = {
    product_business_line: productBusinessLine,
    client_business_line: clientBusinessLine,
    shop_product_model: !!externalProductId,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
    metricsTags,
  });
};

/**
 *
 *
 * @param {*} state
 */
export const trackNewEntryPointsCardView = ({
  fallbackSkuId,
  selectedSkuId,
  source,
  subSource,
  skipEventTracking,
}) => {
  if (skipEventTracking) {
    return;
  }
  const actionType = 'view';
  const actionName = 'new_entry_points_card_view';

  const directBuyContext = {
    sku_id: selectedSkuId || fallbackSkuId,
    source,
    subSource,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
  });
};

/**
 *
 *
 * @param {*} state
 */
export const trackNewEntryPointsCardClick = ({
  fallbackSkuId,
  selectedSkuId,
  source,
  subSource,
}) => {
  const actionType = 'click';
  const actionName = 'new_entry_points_card_click';

  const directBuyContext = {
    sku_id: selectedSkuId || fallbackSkuId,
    source,
    subSource,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
  });
};

export const trackStyleQuizModalCtaClick = ({
  fallbackSkuId,
  selectedSkuId,
  source,
  subSource,
}) => {
  const actionType = 'click';
  const actionName = 'new_entry_points_modal_click';

  const directBuyContext = {
    sku_id: selectedSkuId || fallbackSkuId,
    source,
    subSource,
  };

  trackProductEvent({
    eventName,
    canonicalName,
    actionType,
    actionName,
    actionDetail,
    directBuyContext,
  });
};
